import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle2, TrendingUp, PartyPopper } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money, dateFmt } from "@/lib/format";

export default function Investments() {
  const [packages, setPackages] = useState([]);
  const [invs, setInvs] = useState([]);
  const [balance, setBalance] = useState(0);
  const [busy, setBusy] = useState("");
  const [success, setSuccess] = useState(null);

  const load = async () => {
    const [pkg, dash] = await Promise.all([api.get("/plan/packages"), api.get("/me/dashboard")]);
    setPackages(pkg.data);
    setInvs(dash.data.investments);
    setBalance(dash.data.wallet.deposit_balance);
  };

  useEffect(() => { load().catch(() => toast.error("Load failed")); }, []);

  const buy = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/me/buy", { package_id: id });
      setSuccess({ name: data.package_name, amount: data.amount });
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    setBusy("");
  };

  return (
    <div className="space-y-6" data-testid="investments-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold">Investments</h1>
        <div className="glass rounded-xl px-5 py-3">
          <span className="text-xs text-slate-400 uppercase tracking-wide">Available Balance</span>
          <div className="font-mono-fin text-xl font-bold gold-text">{money(balance)}</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {packages.map((p) => (
          <div key={p.id} data-testid={`buy-package-${p.id}`}
            className={`rounded-2xl p-5 flex flex-col ${p.popular ? "bg-gradient-to-b from-[#18150B] to-[#0D0E12] border-2 border-amber-400" : "bg-[#0D0E12] border border-amber-500/20"}`}>
            <div className="font-display font-semibold text-amber-300">{p.name}</div>
            <div className="font-mono-fin text-3xl font-extrabold mt-2 gold-text">${p.amount}</div>
            <ul className="mt-4 space-y-1.5 text-xs text-slate-300 flex-1">
              <li className="flex gap-1.5 items-center"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> {p.roi_min.toFixed(2)}%–{p.roi_max.toFixed(2)}% Daily ROI</li>
              <li className="flex gap-1.5 items-center"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> 2X ROI Cap</li>
            </ul>
            <button data-testid={`invest-btn-${p.id}`} disabled={busy === p.id} onClick={() => buy(p.id)}
              className="mt-4 gold-btn py-2.5 rounded-xl font-bold text-sm disabled:opacity-60">
              {busy === p.id ? "Processing..." : "Invest"}
            </button>
          </div>
        ))}
      </div>

      <div className="glass rounded-2xl p-6" data-testid="my-investments">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-400" /> My Active Investments</h3>
        {invs.length === 0 ? (
          <p className="text-slate-500 text-sm">No investments yet. Buy a package to start earning daily ROI.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
                  <th className="text-left py-3">Package</th><th className="text-left py-3">Principal</th>
                  <th className="text-left py-3">ROI Earned</th><th className="text-left py-3">Status</th><th className="text-left py-3">Started</th>
                </tr>
              </thead>
              <tbody>
                {invs.map((i) => (
                  <tr key={i.id} className="border-b border-slate-800/60">
                    <td className="py-3 font-medium">{i.package_name}</td>
                    <td className="py-3 font-mono-fin">{money(i.principal)}</td>
                    <td className="py-3 font-mono-fin text-emerald-400">{money(i.roi_earned)}</td>
                    <td className="py-3"><span className={`text-xs px-2 py-1 rounded ${i.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>{i.status}</span></td>
                    <td className="py-3 text-slate-400 text-xs">{dateFmt(i.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {success && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/75 backdrop-blur-sm p-4" data-testid="buy-success-modal" onClick={() => setSuccess(null)}>
          <div className="relative glass border border-amber-500/40 rounded-3xl w-full max-w-md p-8 text-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="gold-glow top-0 left-1/2 -translate-x-1/2" />
            <div className="relative">
              <div className="w-20 h-20 rounded-full gold-btn grid place-items-center mx-auto mb-5 animate-bounce">
                <PartyPopper className="w-10 h-10 text-black" />
              </div>
              <h2 className="font-display text-3xl font-extrabold">Congratulations! 🎉</h2>
              <p className="text-slate-300 mt-3">
                You've successfully activated the <span className="gold-text font-bold">{success.name}</span> package worth
              </p>
              <div className="font-mono-fin text-4xl font-extrabold gold-text my-3">{money(success.amount)}</div>
              <p className="text-sm text-slate-400">Your daily ROI starts earning from the next payout cycle. Welcome aboard! 🚀</p>
              <button data-testid="buy-success-close" onClick={() => setSuccess(null)}
                className="mt-6 gold-btn w-full py-3.5 rounded-xl font-bold">Awesome, let's go!</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
